import React, { useMemo, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import parse from 'html-react-parser';
import { ModuleDraft } from '../../../../../../../../components/admin3/newProgram/masterclass/types';
import { ModuleStructure } from '../../../../../aimd-v2/types';
import ModuleSidebar from '../../../../../aimd-v2/components/ModuleSidebar';
import { diffCourse, lessonKey, plural, quoteTitles } from './diff';
import { formatDateTime } from '../../../../../../../../utils/formatDate';
import { useCourseVersioning, VersionPreviewEntry } from './CourseVersioningContext';

const noop = () => {};

/**
 * What the preview reads. The Designer's CourseVersioningContext by default;
 * the AIMD wizard passes its own source (generation versions mapped to the
 * same module / lesson shape).
 */
export interface VersionPreviewSource {
  previewEntry: VersionPreviewEntry | null;
  resume: () => void;
  restore: () => void;
  /** Restore is unavailable (another admin holds the edit lock, a restore is running). */
  readOnly: boolean;
  lockHolder: { name: string } | null;
}

interface VersionPreviewProps {
  /** The editor's current modules, for the "compared with" summary. */
  draft: ModuleDraft[];
  /** Defaults to the Designer's context. */
  source?: VersionPreviewSource;
  /** Tooltip on a disabled Restore; defaults to the edit-lock holder copy. */
  restoreDisabledReason?: string;
}

const looksLikeHtml = (s: string) => /<\/?[a-z][\s\S]*>/i.test(s);

/**
 * Read-only view of an older version (PRD A5). Rendered by DesignBuilder in
 * place of the editor from the version's snapshot (GET versions/{id}, mapped
 * through the Designer's own template mapper); the editor's own state stays
 * mounted in memory, so Resume current returns exactly what the admin had —
 * no reload, no refetch, no AI generation. Nothing here writes into the
 * editor: Restore goes through the API and the draft is reloaded afterwards.
 */
export function VersionPreview({ draft, source, restoreDisabledReason }: VersionPreviewProps) {
  const ctx = useCourseVersioning();
  const v: VersionPreviewSource | null = source ?? ctx;
  const entry = v?.previewEntry;
  const snapshot = useMemo(() => entry?.snapshot || [], [entry]);
  const [sel, setSel] = useState<{ m: number; l: number }>({ m: 0, l: 0 });

  const summary = useMemo(() => {
    if (!entry) return '';
    const d = diffCourse(draft, snapshot);
    const bits: string[] = [];
    if (d.removed.length) bits.push(`${quoteTitles(d.removed)} isn’t in this version`);
    if (d.added.length) bits.push(`${quoteTitles(d.added)} was later removed`);
    if (d.reworded.length) bits.push(`${plural(d.reworded.length, 'lesson reads', 'lessons read')} differently`);
    if (d.quiz.length) bits.push(`${plural(d.quiz.length, 'quiz differs', 'quizzes differ')}`);
    if (d.sections.length)
      bits.push(`case studies or Roleplay differ in ${plural(d.sections.length, 'lesson', 'lessons')}`);
    return bits.length ? `Compared with your current draft: ${bits.join(' · ')}.` : 'Identical to your current draft.';
  }, [entry, draft, snapshot]);

  if (!v || !entry) return null;

  const module = snapshot[sel.m] || snapshot[0];
  const lesson = module?.lessons[sel.l] || module?.lessons[0];
  const inDraft = draftLessonKeys(draft);
  const when = formatDateTime(entry.at);

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1, gap: 2 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1.5,
          borderRadius: '10px',
          background: 'rgba(247, 148, 32, 0.1)',
          border: '1px solid rgba(247, 148, 32, 0.45)'
        }}
      >
        <VisibilityOutlinedIcon sx={{ color: '#F79420' }} />
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 700, fontFamily: 'Inter', color: '#FDFDFD' }}>
            Viewing “{entry.label}” from {when}
            {entry.author ? ` (${entry.author})` : ''}, read-only
          </Typography>
          <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: '#B6B6B6' }}>
            {summary} Your current draft is untouched.
          </Typography>
        </Box>
        <Button
          onClick={v.resume}
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: 13,
            color: '#FDFDFD',
            border: '1px solid #404040',
            borderRadius: '6px',
            px: 2,
            '&:hover': { borderColor: '#F79420', background: 'transparent' }
          }}
        >
          Resume Current
        </Button>
        <Button
          onClick={v.restore}
          disabled={v.readOnly}
          title={
            v.readOnly
              ? restoreDisabledReason || `${v.lockHolder?.name || 'Another admin'} is editing this program`
              : undefined
          }
          sx={{
            textTransform: 'none',
            fontFamily: 'Inter',
            fontWeight: 600,
            fontSize: 13,
            color: '#FDFDFD',
            borderRadius: '6px',
            px: 2,
            background: 'linear-gradient(180deg, #F79420 0%, #F15A26 100%)',
            '&:hover': { background: 'linear-gradient(180deg, #FFA135 0%, #F46A32 100%)' },
            '&.Mui-disabled': { color: '#868686', background: '#2A2A2A' }
          }}
        >
          Restore This Version
        </Button>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, minHeight: 0, flex: 1 }}>
        <Box sx={{ width: 320, flexShrink: 0, overflowY: 'auto', borderRight: '1px solid #2A2A2A', pr: 2 }}>
          <ModuleSidebar
            programName=""
            hideProgramName
            showReasoning={false}
            readOnly
            modules={snapshot as unknown as ModuleStructure[]}
            selectedModuleIdx={sel.m}
            selectedLessonIdx={sel.l}
            onSelectModule={(m) => setSel({ m, l: 0 })}
            onSelectLesson={(m, l) => setSel({ m, l })}
            onReorder={noop}
            onRenameModule={noop}
            onRenameLesson={noop}
            onAddModule={noop}
            onAddLesson={noop}
            onDeleteModule={noop}
            onDeleteLesson={noop}
            onReasoningClick={noop}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', pt: 1, pb: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {lesson ? (
            <>
              <Box>
                <Typography sx={{ fontSize: 13, fontFamily: 'Inter', color: '#868686' }}>{module?.title}</Typography>
                <Typography sx={{ fontSize: 24, fontWeight: 700, fontFamily: 'Inter', color: '#FDFDFD' }}>
                  {lesson.title}
                </Typography>
                {!inDraft.has(lessonKey(lesson)) && (
                  <Typography sx={{ mt: 0.5, fontSize: 12, fontFamily: 'Inter', color: '#F7A94A' }}>
                    This lesson isn’t in your current draft.
                  </Typography>
                )}
              </Box>
              <Box
                sx={{
                  p: 2.5,
                  borderRadius: '10px',
                  border: '1px solid #2A2A2A',
                  background: '#161616',
                  color: '#DADADA',
                  fontFamily: 'Inter',
                  fontSize: 14,
                  lineHeight: '24px',
                  whiteSpace: looksLikeHtml(lesson.description || '') ? 'normal' : 'pre-wrap',
                  '& img, & video': { maxWidth: '100%' }
                }}
              >
                {lesson.description
                  ? looksLikeHtml(lesson.description)
                    ? parse(lesson.description)
                    : lesson.description
                  : 'No Lesson Body in this version.'}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 2 }}>
                <ReadOnlyCard
                  label="Case studies"
                  value={
                    lesson.elliott?.caseStudiesEnabled === false
                      ? 'Switched off'
                      : lesson.elliott?.caseStudies?.length
                      ? lesson.elliott.caseStudies.map((c) => c.title).join(' · ')
                      : 'None'
                  }
                />
                <ReadOnlyCard
                  label="Roleplay"
                  value={
                    lesson.elliott?.simulationEnabled === false
                      ? 'Switched off'
                      : lesson.elliott?.simulationRubric?.brief || 'None'
                  }
                />
                <ReadOnlyCard
                  label="Quiz"
                  value={
                    lesson.elliott?.quizEnabled === false
                      ? 'Switched off'
                      : lesson.multipleChoiceQuestions.length
                      ? plural(lesson.multipleChoiceQuestions.length, 'question', 'questions')
                      : 'None'
                  }
                />
              </Box>
              {lesson.multipleChoiceQuestions.length > 0 && lesson.elliott?.quizEnabled !== false && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {lesson.multipleChoiceQuestions.map((q, i) => (
                    <Typography key={q.id || i} sx={{ fontSize: 13, fontFamily: 'Inter', color: '#B6B6B6' }}>
                      {i + 1}. {q.prompt}
                    </Typography>
                  ))}
                </Box>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 14, fontFamily: 'Inter', color: '#B6B6B6' }}>
              This version has no lessons.
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function draftLessonKeys(modules: ModuleDraft[]): Set<string> {
  const keys = new Set<string>();
  modules.forEach((m) => m.lessons.forEach((l) => keys.add(lessonKey(l))));
  return keys;
}

function ReadOnlyCard({ label, value }: { label: string; value: string }) {
  return (
    <Box sx={{ p: 2, borderRadius: '10px', border: '1px solid #2A2A2A', background: '#161616' }}>
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          fontFamily: 'Inter',
          color: '#868686',
          mb: 1
        }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'Inter', color: '#FDFDFD', lineHeight: '20px' }}>
        {value}
      </Typography>
    </Box>
  );
}
